import React, {useContext, useState, useEffect} from 'react';
import styles from '../tables.module.css';
import swapStyles from './SwapRobotsModal.module.css';
import {PRISMAX_BACKEND_URL } from "../../../configs/config";
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import {isSessionExpiredError} from '../../../utils/authErrors';

const SwapRobotsModal = ({robots, onClose, onSwapSuccess }) => {
    const { showNotification, handleAdminSessionExpired } = useContext(SwapMainAppContext);

    const [robotIdA, setRobotIdA] = useState(robots[1]?.robot_id || '');
    const [robotIdB, setRobotIdB] = useState(robots[2]?.robot_id || '');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (robotIdA === robotIdB) {
            showNotification('error', 'Please select two different robots to swap.');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/swap-robots`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({robot_id_a: robotIdA, robot_id_b: robotIdB})
            });
            const result = await response.json();

            if (result.success) {
                await onSwapSuccess();
                showNotification('success', result.msg);
                onClose();
            } else {
                if (isSessionExpiredError(result.msg)) {
                    handleAdminSessionExpired?.();
                    return;
                }
                showNotification('error', result.msg || 'Failed to swap robots. Please try again.');
            }
        } catch (err) {
            showNotification('error', 'Failed to swap robots. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={swapStyles.modalOverlay}>
            <div className={swapStyles.modal}>
                <h3>Swap Robots</h3>
                <p className={swapStyles.description}>
                    Swaps the name, slug, and class between two robots. Both robots must be paused before swapping.
                </p>

                <div className={swapStyles.formGroup}>
                    <label>Robot A</label>
                    <select
                        className={swapStyles.select}
                        value={robotIdA}
                        onChange={e => setRobotIdA(e.target.value)}
                        disabled={submitting}
                    >
                        {robots.map(r => (
                            <option key={r.robot_id} value={r.robot_id}>
                                {r.robot_id} — {r.robot_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={swapStyles.formGroup}>
                    <label>Robot B</label>
                    <select
                        className={swapStyles.select}
                        value={robotIdB}
                        onChange={e => setRobotIdB(e.target.value)}
                        disabled={submitting}
                    >
                        {robots.map(r => (
                            <option key={r.robot_id} value={r.robot_id}>
                                {r.robot_id} — {r.robot_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={swapStyles.modalButtons}>
                    <button className={swapStyles.cancelButton} onClick={onClose} disabled={submitting}>
                        Cancel
                    </button>
                    <button className={swapStyles.submitButton} onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Submitting…' : 'Swap'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const RobotTeleStatus = () => {
    const { showNotification, handleAdminSessionExpired } = useContext(SwapMainAppContext);
    const [robots, setRobots] = useState([]);
    const [youtubeStreamDraft, setYoutubeStreamDraft] = useState('');
    const [youtubeModalRobot, setYoutubeModalRobot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState({});
    const [swapModalOpen, setSwapModalOpen] = useState(false);

    // Fetch robot status data
    const fetchRobotStatus = async () => {
        try {
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(`${PRISMAX_BACKEND_URL}/get-robot-status`, {
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch robot status');
            }
            const data = await response.json();
            if (data.robots) {
                setRobots(data.robots);
            } else {
                throw new Error('Invalid data format');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRobotStatus();
    }, []);

    // Handle status change
    const handleStatusChange = async (robotId, newStatus) => {
        setUpdating(prev => ({...prev, [robotId]: true}));

        try {
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/set-live-paused`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    robot_id: robotId,
                    live_paused: newStatus === 'true'
                })
            });
            const data = await response.json();

            if (data.success) {
                // Update local state
                setRobots(prev => prev.map(robot =>
                    robot.robot_id === robotId
                        ? {...robot, live_paused: newStatus === 'true'}
                        : robot
                ));
            } else {
                alert(data.msg || 'Failed to update robot status');
                // Refresh to get actual status
                fetchRobotStatus();
            }
        } catch (err) {
            alert('Failed to update robot status: ' + err.message);
            fetchRobotStatus();
        } finally {
            setUpdating(prev => ({...prev, [robotId]: false}));
        }
    };

    // Handle global live change
    const handleGlobalLiveChange = async (robotId, newStatus) => {
        setUpdating(prev => ({...prev, [robotId]: true}));

        try {
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/set-global-live`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    robot_id: robotId,
                    is_global_live: newStatus === 'true'
                })
            });
            const data = await response.json();

            if (data.success) {
                const isGlobalLive = newStatus === 'true';
                setRobots(prev => prev.map(robot => {
                    if (isGlobalLive) {
                        return {
                            ...robot,
                            is_global_live: robot.robot_id === robotId
                        };
                    }
                    return robot.robot_id === robotId
                        ? {...robot, is_global_live: false}
                        : robot;
                }));
            } else {
                alert(data.msg || 'Failed to update global live status');
                // Refresh to get actual status
                fetchRobotStatus();
            }
        } catch (err) {
            alert('Failed to update global live status: ' + err.message);
            fetchRobotStatus();
        } finally {
            setUpdating(prev => ({...prev, [robotId]: false}));
        }
    };

    const openYoutubeStreamModal = (robot) => {
        setYoutubeModalRobot(robot);
        setYoutubeStreamDraft(robot.youtube_stream_id || '');
    };

    const closeYoutubeStreamModal = () => {
        setYoutubeModalRobot(null);
        setYoutubeStreamDraft('');
    };

    const handleYoutubeStreamIdSubmit = async () => {
        if (!youtubeModalRobot) {
            return;
        }

        const robotId = youtubeModalRobot.robot_id;
        const nextStreamId = youtubeStreamDraft.trim();
        setUpdating(prev => ({...prev, [robotId]: true}));

        try {
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/set-youtube-stream-id`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    robot_id: robotId,
                    youtube_stream_id: nextStreamId
                })
            });
            const data = await response.json();

            if (data.success) {
                setRobots(prev => prev.map(robot =>
                    robot.robot_id === robotId
                        ? {...robot, youtube_stream_id: data.youtube_stream_id || ''}
                        : robot
                ));
                closeYoutubeStreamModal();
                showNotification('success', data.msg || 'YouTube stream ID updated successfully');
            } else {
                if (isSessionExpiredError(data.msg)) {
                    handleAdminSessionExpired?.();
                    return;
                }
                showNotification('error', data.msg || data.error || 'Failed to update YouTube stream ID');
                fetchRobotStatus();
            }
        } catch (err) {
            showNotification('error', 'Failed to update YouTube stream ID: ' + err.message);
            fetchRobotStatus();
        } finally {
            setUpdating(prev => ({...prev, [robotId]: false}));
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading robot status...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>Robot Live Status Control</h2>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>Robot ID</th>
                        <th>Robot Name</th>
                        <th>Live Paused Status</th>
                        <th>Global Live Stream</th>
                        <th>YouTube Stream ID</th>
                    </tr>
                    </thead>
                    <tbody>
                    {robots.length > 0 ? (
                        robots.map((robot) => (
                            <tr key={robot.robot_id}>
                                <td>{robot.robot_id}</td>
                                <td>{robot.robot_name}</td>
                                <td>
                                    <select
                                        value={robot.live_paused ? 'true' : 'false'}
                                        onChange={(e) => handleStatusChange(robot.robot_id, e.target.value)}
                                        disabled={updating[robot.robot_id]}
                                        className={styles.select}
                                    >
                                        <option value="false">Active</option>
                                        <option value="true">Paused</option>
                                    </select>
                                </td>
                                <td>
                                    <select
                                        value={robot.is_global_live ? 'true' : 'false'}
                                        onChange={(e) => handleGlobalLiveChange(robot.robot_id, e.target.value)}
                                        disabled={updating[robot.robot_id]}
                                        className={styles.select}
                                    >
                                        <option value="false">Off</option>
                                        <option value="true">On</option>
                                    </select>
                                </td>
                                <td>
                                    <div className={styles.youtubeStreamDisplay}>
                                        <span className={styles.streamIdValue}>
                                            {robot.youtube_stream_id || 'Not set'}
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.inlineSubmitButton}
                                            onClick={() => openYoutubeStreamModal(robot)}
                                            disabled={updating[robot.robot_id]}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="5" className={styles.noData}>
                                No robots found
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '1rem'}}>
                <button className={styles.addButton} onClick={() => setSwapModalOpen(true)}>
                    Swap Robots
                </button>
            </div>

            {swapModalOpen && (
                <SwapRobotsModal
                    robots={robots}
                    onClose={() => setSwapModalOpen(false)}
                    onSwapSuccess={fetchRobotStatus}
                />
            )}

            {youtubeModalRobot && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>Update YouTube Stream ID</h3>
                        <p className={styles.modalDescription}>
                            Robot {youtubeModalRobot.robot_id} — {youtubeModalRobot.robot_name}
                        </p>

                        <div className={styles.formGroup}>
                            <label>YouTube Stream ID</label>
                            <input
                                className={styles.input}
                                value={youtubeStreamDraft}
                                onChange={(e) => setYoutubeStreamDraft(e.target.value)}
                                placeholder={youtubeModalRobot.youtube_stream_id || 'Enter YouTube Stream ID'}
                                disabled={updating[youtubeModalRobot.robot_id]}
                            />
                            <p className={styles.fieldHint}>
                                Leave blank and submit to clear the current stream ID.
                            </p>
                        </div>

                        <div className={styles.modalButtons}>
                            <button
                                className={styles.cancelButton}
                                onClick={closeYoutubeStreamModal}
                                disabled={updating[youtubeModalRobot.robot_id]}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.confirmButton}
                                onClick={handleYoutubeStreamIdSubmit}
                                disabled={updating[youtubeModalRobot.robot_id]}
                            >
                                {updating[youtubeModalRobot.robot_id] ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RobotTeleStatus;
